import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Row, Col } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { RowSelectEventArgs } from '@syncfusion/ej2-react-grids'
import { useSelector } from 'react-redux'
import { RootState } from '@store/index'
import { ICommonProcedure } from '@dataTypes/appointment.type'
import { getAllProcedures } from '@services/api/procedure.api'
import { SearchWithFilter } from '@components/common'

type ApptCommonProcedureModalProps = {
  isOpen: boolean
  closeModal: () => void
  commonProcedures: ICommonProcedure[]
  saveCommonProcedure: (procId: string) => void
  isSaving?: boolean
}

const ApptCommonProcedureModal: React.FC<ApptCommonProcedureModalProps> = ({
  isOpen,
  closeModal,
  saveCommonProcedure,
  commonProcedures,
  isSaving = false,
}) => {
  const proceduresData = useSelector((state: RootState) => state.procedure.data)
  const [procedures, setProcedures] = useState<any[]>(proceduresData || [])
  const [searchProcedure, setSearchProcedure] = useState('')
  const [searchProcedureResults, setSearchProcedureResults] = useState<any[]>(
    [],
  )
  const [selectedProcedureId, setSelectedProcedureId] = useState<string | null>(
    null,
  )

  useEffect(() => {
    if (procedures.length === 0) {
      getAllProcedures().then((res) => {
        if ('procedures' in res.data) {
          setProcedures(res.data.procedures || [])
        }
      })
    }
  }, [])

  useEffect(() => {
    const input = document.querySelector(
      'input.search-procedure',
    ) as HTMLInputElement
    input?.focus()
  }, [isOpen])

  useEffect(() => {
    const timer = setTimeout(() => {
      const results = procedures.filter((x) => {
        const isNotExisting = !commonProcedures.some(
          (cp) => cp.procedure?.procedure_code === x.procedure_code,
        )
        const matchesSearch =
          x.procedure_code
            ?.toLowerCase()
            .includes(searchProcedure.trim().toLowerCase()) ||
          x.description
            ?.toLowerCase()
            .includes(searchProcedure.trim().toLowerCase())

        return isNotExisting && matchesSearch
      })
      setSearchProcedureResults(results)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchProcedure])

  const columnsProcedures = useMemo(() => {
    return [
      {
        field: 'space',
        headerText: '',
        width: '12px',
      },
      {
        field: 'procedure_code',
        headerText: 'Code',
        width: '68px',
      },
      {
        field: 'description',
        headerText: 'Description',
        width: '412px',
      },
    ].map((column) => ({
      ...column,
    }))
  }, [])

  const setDefaultValues = (): void => {
    setSearchProcedure('')
    setSearchProcedureResults([])
    setSelectedProcedureId(null)
  }

  const handleSearchProcedure = (e: any): void => {
    setSearchProcedure(e.target.value.toLowerCase())
  }

  const handleSave = (): void => {
    if (selectedProcedureId) {
      saveCommonProcedure(selectedProcedureId)
      setDefaultValues()
    }
  }

  const onModalClose = (): void => {
    setDefaultValues()
    closeModal()
  }

  const onSelectProcedure = (args: RowSelectEventArgs): void => {
    if (args?.data && typeof args.data === 'object') {
      setSelectedProcedureId(
        (args.data as { procedure_id: string }).procedure_id,
      )
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={onModalClose}
      size="sm"
      className="add-common-procedure-modal"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Add favorite procedure</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col>
            <SearchWithFilter
              searchIcon="mgc mgc_search_2_line"
              placeholder="Search"
              searchQuery={searchProcedure}
              onChange={handleSearchProcedure}
              onFilterChange={() => {}}
              filters={{}}
              showFilter={false}
              className="search-procedure-filter"
            />
          </Col>
        </Row>
        <Row className="mt-2">
          <Col>
            <div className="grid-procedures-container">
              <DataGridComponent
                data={searchProcedureResults}
                columns={columnsProcedures}
                rowHeight={36}
                allowTextWrap={true}
                rowSelected={onSelectProcedure}
                selectionSettings={{
                  type: 'Single',
                  mode: 'Row',
                  enableToggle: false,
                }}
                onRowDoubleClick={handleSave}
                allowPaging={true}
                pageSize={10}
                pageCount={5}
              />
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          size="md"
          className="button-secondary"
          onClick={onModalClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          size="md"
          className="button-primary"
          onClick={handleSave}
          disabled={!selectedProcedureId || isSaving}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ApptCommonProcedureModal
